import $ from 'jquery';

import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import {NotePart} from 'notebook/util/NoteUtilConstants';

import {PendingCut} from '../EditorTypes';

export function isPendingCut(pendingCuts: Array<PendingCut>, cid: string, provenanceUuid?: string): boolean {
  return pendingCuts.some(
    (cut) => cut.cid === cid && (cut.provenanceUuid == null || cut.provenanceUuid === provenanceUuid)
  );
}

// Finds the NoteParts corresponding to the pending cuts and validates they still match.
export function findNotePartsForPendingCuts(
  noteParts: Array<NotePart>,
  pendingCuts: Array<PendingCut>,
  getCid: (part: NotePart) => string
): Array<NotePart> | null {
  const cutStart = noteParts.findIndex((part) => getCid(part) === pendingCuts[0].cid);
  if (cutStart === -1) {
    return null;
  }

  const cutItems = noteParts.slice(cutStart, cutStart + pendingCuts.length);
  if (cutItems.length !== pendingCuts.length) {
    return null;
  }

  const matchPendingCuts = cutItems.every((item, i) => getCid(item) === pendingCuts[i].cid);
  if (!matchPendingCuts) {
    return null;
  }

  return cutItems;
}
/**
 * Computes the range to be replaced when for atomic cut/paste
 * Returns arguments that will be passed into a single replaceItems(index, count, ...items) call.
 */
export function computeAtomicMoveSlice(
  noteParts: Array<NotePart>,
  pendingCuts: Array<PendingCut>,
  pasteStart: number,
  pasteEnd: number,
  addedItems: Array<NotePart>,
  getCid: (part: NotePart) => string
): {rangeStart: number; rangeDeleteCount: number; reorderedItems: Array<NotePart>} {
  const cutStart = noteParts.findIndex((part) => getCid(part) === pendingCuts[0].cid);
  const cutEnd = cutStart + pendingCuts.length - 1;

  let reorderedItems: Array<NotePart>;
  if (cutStart <= pasteStart) {
    reorderedItems = [...noteParts.slice(cutEnd + 1, pasteStart), ...addedItems];
  } else {
    reorderedItems = [...addedItems, ...noteParts.slice(pasteEnd + 1, cutStart)];
  }

  const rangeStart = Math.min(cutStart, pasteStart);
  const rangeEnd = Math.max(cutEnd, pasteEnd);
  const rangeDeleteCount = rangeEnd - rangeStart + 1;

  return {rangeStart, rangeDeleteCount, reorderedItems};
}

export function buildPendingCutsFromItems(
  items: Array<NotePart>,
  getCid: (item: NotePart) => string
): Array<PendingCut> {
  return items.map((item) => ({
    cid: getCid(item),
    provenanceUuid: (item.item as {provenanceUuid?: string} | undefined)?.provenanceUuid,
  }));
}

function getClipboardMediocreItems(clipboardData: DataTransfer, editorName: string): JQuery {
  if (!wyciwyp.typesContains(clipboardData.types, 'text/html')) {
    return $();
  }
  const html = wyciwyp.getHTMLData(clipboardData);
  const $data = wyciwyp.jQuerySafeParseHTML(html);
  const itemSelector = `[data-mediocre-editor='${editorName}']`;
  return $data.find(itemSelector).addBack(itemSelector);
}

export function extractFileIdsFromClipboard(
  clipboardData: DataTransfer,
  editorName: string
): Array<string | null> {
  return Array.from(getClipboardMediocreItems(clipboardData, editorName)).map(
    (el) => $(el).attr('data-mediocre-file-id') ?? null
  );
}

function extractCidsFromClipboard(clipboardData: DataTransfer, editorName: string): Array<string> {
  return Array.from(getClipboardMediocreItems(clipboardData, editorName)).map((el) => {
    const json = $(el).attr('data-mediocre-item');
    return (JSON.parse(json) as {cid: string}).cid;
  });
}

export function doesClipboardMatchPendingCuts(
  clipboardData: DataTransfer,
  editorName: string,
  pendingCuts: Array<PendingCut>
): boolean {
  const clipboardCids = extractCidsFromClipboard(clipboardData, editorName);
  if (clipboardCids.length !== pendingCuts.length) {
    return false;
  }
  return clipboardCids.every((cid, i) => cid === pendingCuts[i].cid);
}
